<template>
  <el-config-provider :locale="elementLocale">
    <div id="app">
      <router-view/>
    </div>
  </el-config-provider>
</template>

<script setup>
import { computed, watch, onMounted } from 'vue';
import { useStore } from 'vuex';
import zhCn from 'element-plus/es/locale/lang/zh-cn';
import zhTw from 'element-plus/es/locale/lang/zh-tw';
import { setAppLocale } from '@/i18n';
import {
  FIXED_TABLE_PAGE_BACKGROUND,
  mixHex,
  readableControlForeground,
  resolveBusinessButtonPalette,
  resolveThemePalette,
  resolveTableTextColors,
} from '@/utils/themePresets.mjs';

const store = useStore();

// 获取 store 中的颜色配置
const themeColor = computed(() => store.state.basic.themeColor);
const formLabelColor = computed(() => store.state.basic.formLabelColor);
const buttonColor = computed(() => store.state.basic.buttonColor);
const tableTheme = computed(() => store.state.basic.tableTheme);
const locale = computed(() => store.state.basic.locale);
const elementLocale = computed(() => locale.value === 'zh-HK' ? zhTw : zhCn);

const tableThemeVariables = {
  surfaceBackground: '--table-surface-background',
  headerBackground: '--table-header-bg',
  headerTextColor: '--table-header-color',
  headerFontFamily: '--table-header-font-family',
  headerFontSize: '--table-header-font-size',
  headerFontWeight: '--table-header-font-weight',
  headerLineHeight: '--table-header-line-height',
  bodyBackground: '--table-body-background',
  bodyTextColor: '--table-body-color',
  bodyFontFamily: '--table-body-font-family',
  bodyFontSize: '--table-body-font-size',
  bodyLineHeight: '--table-body-line-height',
  borderColor: '--table-border-color',
  borderWidth: '--table-border-width',
  rowHoverBackground: '--table-row-hover-bg',
  scrollbarTrackColor: '--table-scrollbar-track-color',
  scrollbarThumbColor: '--table-scrollbar-thumb-color',
  scrollbarThumbHoverColor: '--table-scrollbar-thumb-hover-color',
  scrollbarSize: '--table-scrollbar-size',
};

const businessButtonPropertyVariables = {
  background: 'bg',
  text: 'text',
  border: 'border',
  hoverBackground: 'hover-bg',
  hoverText: 'hover-text',
  hoverBorder: 'hover-border',
  activeBackground: 'active-bg',
  activeText: 'active-text',
  activeBorder: 'active-border',
};

const businessButtonRoles = [
  'primary',
  'secondary',
  'success',
  'warning',
  'danger',
  'info',
  'disabled',
];

// 更新全局 CSS 变量
const updateTheme = () => {
  const root = document.documentElement;
  const palette = resolveThemePalette(themeColor.value);
  
  // 设置主题色（同步给 Element Plus 的 primary 颜色）
  if (themeColor.value) {
    root.style.setProperty('--el-color-primary', themeColor.value);
    root.style.setProperty('--el-color-primary-dark-2', palette.primaryDark);
    root.style.setProperty('--el-color-primary-light-3', mixHex(palette.primary, '#FFFFFF', 0.3));
    root.style.setProperty('--el-color-primary-light-5', mixHex(palette.primary, '#FFFFFF', 0.5));
    root.style.setProperty('--el-color-primary-light-7', mixHex(palette.primary, '#FFFFFF', 0.7));
    root.style.setProperty('--el-color-primary-light-8', mixHex(palette.primary, '#FFFFFF', 0.8));
    root.style.setProperty('--el-color-primary-light-9', palette.primaryLight);
    root.style.setProperty('--shell-primary', palette.primary);
    root.style.setProperty('--shell-primary-dark', palette.primaryDark);
    root.style.setProperty('--shell-primary-light', palette.primaryLight);
    root.style.setProperty('--shell-sidebar-bg', palette.sideBackground);
    root.style.setProperty('--shell-sidebar-text', palette.sideText);
    root.style.setProperty('--shell-sidebar-hover', palette.sideHover);
    root.style.setProperty('--shell-shadow-color', palette.shadowColor);
  }
  
  // 设置表单 Label 颜色
  if (formLabelColor.value) {
    root.style.setProperty('--form-label-color', formLabelColor.value);
  }
  
  // 设置按钮颜色（如果与主题色不同，可以通过自定义类应用）
  if (buttonColor.value) {
    const buttonPalette = resolveThemePalette(buttonColor.value);
    root.style.setProperty('--button-color-primary', buttonColor.value);
    root.style.setProperty('--button-color-hover', buttonPalette.primaryDark);
    root.style.setProperty('--button-color-foreground', readableControlForeground(buttonColor.value));
    // 更新 Element Plus 按钮的主色调
    root.style.setProperty('--el-color-primary-button', buttonColor.value);
  }

  const businessButtonPalette = resolveBusinessButtonPalette(themeColor.value, buttonColor.value);
  businessButtonRoles.forEach((role) => {
    const rolePalette = businessButtonPalette[role];
    Object.entries(businessButtonPropertyVariables).forEach(([property, variableSuffix]) => {
      root.style.setProperty(`--business-button-${role}-${variableSuffix}`, rolePalette[property]);
    });
  });
  root.style.setProperty('--business-button-focus-ring', businessButtonPalette.focusRing);
  root.style.setProperty('--business-button-primary-shadow', businessButtonPalette.primary.boxShadow);
  root.style.setProperty('--business-button-primary-active-shadow', businessButtonPalette.primary.activeBoxShadow);
  root.style.setProperty('--table-page-background', FIXED_TABLE_PAGE_BACKGROUND);

  Object.entries(tableThemeVariables).forEach(([key, variable]) => {
    const value = tableTheme.value?.[key];
    if (value) {
      root.style.setProperty(variable, value);
    }
  });

  // anml 自定义单元格使用派生文字色；普通、悬停和选中背景分别计算。
  const bodyBackground = tableTheme.value?.bodyBackground || '#ffffff';
  const bodyTextColor = tableTheme.value?.bodyTextColor || '#1f2937';
  const rowBackgrounds = {
    body: bodyBackground,
    hover: tableTheme.value?.rowHoverBackground || '#f3f4f6',
    selected: mixHex(bodyBackground, palette.primary, 0.12),
  };
  root.style.setProperty('--table-row-selected-bg', rowBackgrounds.selected);
  Object.entries(rowBackgrounds).forEach(([state, background]) => {
    const colors = resolveTableTextColors(bodyTextColor, background);
    root.style.setProperty(`--table-cell-${state}-primary-color`, colors.primary);
    root.style.setProperty(`--table-cell-${state}-secondary-color`, colors.secondary);
  });
};

// 监听配置变化并实时应用
watch([themeColor, formLabelColor, buttonColor, tableTheme], updateTheme, { immediate: true, deep: true });

watch(locale, (value) => {
  setAppLocale(value);
  document.documentElement.lang = value;
}, { immediate: true });

onMounted(() => {
  updateTheme();
});
</script>

<style>
* {
  margin: 0;
  padding: 0;
}

html, body {
  margin: 0;
  width: 100%;
  height: 100%;
}

#app {
  font-family: Avenir, Helvetica, Arial, sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  text-align: center;
  color: #2c3e50;
  height: 100%;
  overflow: inherit;
}

</style>
